// ============================================================
// RmkActionPanel — 루미큐브 액션 버튼 패널  (SEQ-10214 S5c)
//   TD §3.2 UILayer 액션 버튼 영역 (HTML DOM — Konva 외부)
//   버튼: Draw · EndTurn · Undo · JokerSwap
//   턴 타이머: turnDeadlineTs - NOW countdown
//   TtrActionPanel 패턴 정합 (isMyTurn 가드, disabled reason 콜백)
// ============================================================

import React, { useEffect, useState } from 'react'

export interface RmkActionPanelProps {
  isMyTurn:          boolean
  hubReady:          boolean
  turnDeadlineTs:    number   // epoch ms (0=비활성)
  drawPileRemaining: number
  myHandSize:        number
  // 액션 핸들러
  onDraw:            () => void
  onEndTurn:         () => void
  onUndo:            () => void
  onJokerSwap?:      () => void
  // 비활성 시 토스트 이유 콜백
  onDisabledReason?: (reason: string) => void
}

const RmkActionPanel: React.FC<RmkActionPanelProps> = ({
  isMyTurn,
  hubReady,
  turnDeadlineTs,
  drawPileRemaining,
  myHandSize,
  onDraw,
  onEndTurn,
  onUndo,
  onJokerSwap,
  onDisabledReason,
}) => {
  // 턴 타이머 (1초 갱신)
  const [secondsLeft, setSecondsLeft] = useState<number>(0)

  useEffect(() => {
    if (!turnDeadlineTs || turnDeadlineTs <= 0) {
      setSecondsLeft(0)
      return
    }
    function tick() {
      const diff = Math.max(0, Math.floor((turnDeadlineTs - Date.now()) / 1000))
      setSecondsLeft(diff)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [turnDeadlineTs])

  function guardAction(): boolean {
    if (!hubReady) {
      onDisabledReason && onDisabledReason('서버 연결 중... 잠시 후 다시 시도하세요')
      return false
    }
    if (!isMyTurn) {
      onDisabledReason && onDisabledReason('내 턴이 아닙니다')
      return false
    }
    return true
  }

  function handleDraw() {
    if (!guardAction()) return
    onDraw()
  }

  function handleEndTurn() {
    if (!guardAction()) return
    onEndTurn()
  }

  function handleUndo() {
    if (!guardAction()) return
    onUndo()
  }

  function handleJokerSwap() {
    if (!guardAction()) return
    onJokerSwap && onJokerSwap()
  }

  const timerColor = secondsLeft <= 15 ? '#F44336' : secondsLeft <= 30 ? '#FF9800' : '#4CAF50'
  // SEQ=10222 시간 초과 가드: 90초 타임아웃 후 서버 자동 턴 전환과 desync 방지
  //   turnDeadlineTs>0 + secondsLeft<=0 → 클라가 TurnChanged 받기 전까지 액션 차단 ("내 턴이 아닙니다" 토스트 방지)
  const timeExpired = isMyTurn && turnDeadlineTs > 0 && secondsLeft <= 0
  const actionDisabled = !isMyTurn || !hubReady || timeExpired

  return (
    <div className="rmk-action-panel">
      {/* 턴 상태 표시 */}
      <div className="rmk-turn-status">
        <span className={isMyTurn ? 'rmk-myturn-on' : 'rmk-myturn-off'}>
          {isMyTurn ? '내 턴' : '대기 중'}
        </span>
        {!hubReady && (
          <span className="rmk-connecting" title="서버 연결 중">연결 중...</span>
        )}
      </div>

      {/* 턴 타이머 */}
      {isMyTurn && turnDeadlineTs > 0 && (
        <div className="rmk-timer" style={{ color: timerColor }}>
          {secondsLeft > 0 ? `${secondsLeft}초` : '시간 초과'}
        </div>
      )}

      {/* 덱/손패 정보 */}
      <div className="rmk-pile-info">
        <span>더미: {drawPileRemaining}장</span>
        <span>손패: {myHandSize}장</span>
      </div>

      {/* 액션 버튼 */}
      <div className="rmk-action-buttons">
        <button
          className="rmk-btn rmk-btn-draw"
          disabled={actionDisabled || drawPileRemaining === 0}
          onClick={handleDraw}
          title="더미에서 타일 1장 뽑기 (보드 조작 전에만 가능)"
        >
          뽑기
        </button>

        <button
          className="rmk-btn rmk-btn-undo"
          disabled={actionDisabled}
          onClick={handleUndo}
          title="이번 턴 작업 전체 되돌리기"
        >
          되돌리기
        </button>

        <button
          className="rmk-btn rmk-btn-end"
          disabled={actionDisabled}
          onClick={handleEndTurn}
          title="턴 종료 (배치 검증 — 유효하면 확정, 무효하면 패널티 1장)"
        >
          턴 종료
        </button>

        {onJokerSwap && (
          <button
            className="rmk-btn rmk-btn-joker"
            disabled={actionDisabled}
            onClick={handleJokerSwap}
            title="조커 회수 (첫 진입 30점 완료 후에만 가능)"
          >
            조커 회수
          </button>
        )}
      </div>
    </div>
  )
}

export default RmkActionPanel
